import { useState } from "react";
import { useNavigate } from "react-router-dom";
import DefinirNovaSenha from "../components/auth/DefinirNovaSenha";
import { useAuth } from "../contexts/AuthContext";
import { resolverPaginaInicial } from "../utils/permissions";
import { proximaTela } from "../utils/nextPath";
import { formatApiError } from "../utils/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Eye, EyeOff, ArrowRight, Mail, Lock, Sparkles } from "lucide-react";
import { accentBg } from "../utils/accent";
import { toast } from "../components/ui/sonner";
import ForgotPasswordDialog from "../components/auth/ForgotPasswordDialog";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const { user, login, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  // Já logado com senha provisória (ex.: F5 no meio do passo): cai direto na nova senha.
  const [etapa, setEtapa] = useState(user?.must_change_password ? "nova-senha" : "login");

  // Lido uma vez: depois do login a URL muda e o `next` some.
  const [destino] = useState(proximaTela);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      return toast.error("Campo obrigatório", { description: "Preencha o email." });
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return toast.error("Email inválido", { description: "Digite um endereço de email válido." });
    }
    if (!password) {
      return toast.error("Campo obrigatório", { description: "Preencha a senha." });
    }
    setError("");
    setLoading(true);
    try {
      const logado = await login(email, password);
      if (logado?.must_change_password) {
        setEtapa("nova-senha");
        return;
      }
      navigate(destino || (await resolverPaginaInicial(logado)));
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-white" data-testid="login-page">
      {/* Left - Form */}
      <div className="flex-1 relative flex items-center justify-center p-6 sm:p-12 overflow-hidden">
        {/* G, Barra lateral laranja de cima a baixo (identidade AutEM) */}
        <div
          className="absolute left-0 top-0 bottom-0 w-[8px] z-20"
          style={{ backgroundColor: "#FF9945" }}
          aria-hidden="true"
        />
        {/* A, Padrão de pontos sutil no fundo */}
        <div
          className="absolute inset-0 opacity-60 pointer-events-none"
          style={{
            backgroundImage: "radial-gradient(circle, #94a3b8 1.2px, transparent 1.2px)",
            backgroundSize: "24px 24px",
            maskImage: "radial-gradient(ellipse 60% 60% at 50% 50%, rgba(0,0,0,0.5) 30%, transparent 85%)",
            WebkitMaskImage: "radial-gradient(ellipse 60% 60% at 50% 50%, rgba(0,0,0,0.5) 30%, transparent 85%)",
          }}
          aria-hidden="true"
        />

        <div className="w-full max-w-[420px] relative z-10">
          {/* Brand */}
          <div className="flex items-center gap-3 mb-10">
            <img
              src="/logo_light.png"
              alt="AutEM"
              className="h-12 object-contain"
            />
          </div>

          {etapa === "nova-senha" ? (
            <DefinirNovaSenha
              email={user?.email || email}
              onDone={async () => {
                const atualizado = await refreshUser();
                navigate(destino || (await resolverPaginaInicial(atualizado || user)));
              }}
              onCancel={async () => {
                await logout();
                setPassword("");
                setEtapa("login");
              }}
            />
          ) : (
          <>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Bem-vindo de volta
          </h1>
          <p className="text-gray-500 mb-8">
            Faça login para acessar o painel de controle
          </p>

          {error && (
            <div
              className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm"
              data-testid="login-error"
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate className="space-y-5">
            <div>
              <Label
                htmlFor="email"
                className="text-gray-700 text-sm font-medium mb-2 block"
              >
                Email
              </Label>
              <div className="relative">
                <Mail
                  size={16}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                />
                <Input
                  id="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 rounded-xl border-gray-200 accent-ring pl-11 bg-white"
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div>
              <Label
                htmlFor="password"
                className="text-gray-700 text-sm font-medium mb-2 block"
              >
                Senha
              </Label>
              <div className="relative">
                <Lock
                  size={16}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                />
                <Input
                  id="password"
                  type={showPw ? "text" : "password"}
                  placeholder="Digite sua senha"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 rounded-xl border-gray-200 accent-ring pl-11 pr-12 bg-white"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  onClick={() => setShowPw(!showPw)}
                  data-testid="toggle-password-btn"
                  tabIndex={-1}
                >
                  {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="flex justify-end -mt-2">
              <button
                type="button"
                onClick={() => setForgotOpen(true)}
                className="text-sm font-medium hover:underline"
                style={{ color: "var(--accent)" }}
                data-testid="login-forgot-password-btn"
              >
                Esqueci minha senha
              </button>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-xl text-white font-semibold text-sm transition-all duration-200"
              style={accentBg()}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Entrando...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  Entrar
                  <ArrowRight size={16} />
                </span>
              )}
            </Button>
            <p className="text-center text-xs text-gray-500 mt-3">
              Ao entrar, você concorda com nossos{" "}
              <a
                href="/terms"
                target="_blank"
                rel="noreferrer"
                className="font-medium underline decoration-gray-300 hover:decoration-current"
                style={{ color: "var(--accent)" }}
                data-testid="login-terms-link"
              >
                Termos de Uso
              </a>{" "}
              e{" "}
              <a
                href="/privacy"
                target="_blank"
                rel="noreferrer"
                className="font-medium underline decoration-gray-300 hover:decoration-current"
                style={{ color: "var(--accent)" }}
                data-testid="login-privacy-link"
              >
                Política de Privacidade
              </a>.
            </p>
          </form>
          </>
          )}

          {/* Lead callout, para quem ainda não é cliente */}
          {etapa === "login" && (
          <div
            className="mt-8 rounded-2xl border border-gray-200 bg-gray-50 p-5 flex items-center gap-3"
            data-testid="login-lead-callout"
          >
            <div
              className="h-9 w-9 rounded-full flex items-center justify-center text-white shrink-0"
              style={{ backgroundColor: "var(--accent)" }}
              aria-hidden="true"
            >
              <Sparkles size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 leading-tight">
                Ainda não utiliza o AutEM?
              </p>
              <p className="text-xs text-gray-600 mt-0.5">
                Fale conosco, temos prazer em te ver crescer!
              </p>
            </div>
            <a
              href="mailto:contato@satellitus.com?subject=Quero%20conhecer%20o%20AutEM"
              className="shrink-0 inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-xs font-semibold bg-white border-2 transition-transform hover:scale-[1.02]"
              style={{
                color: "var(--accent)",
                borderColor: "var(--accent)",
              }}
              data-testid="login-lead-cta"
            >
              Fale conosco
              <ArrowRight size={14} />
            </a>
          </div>
          )}

          <p className="mt-8 text-center text-sm text-gray-400">
            AutEM — Feito para quem lidera!
          </p>
          <p className="mt-1 text-center text-[10px] text-gray-300">
            © {new Date().getFullYear()} Satellitus Tecnologia · Todos os direitos reservados
          </p>
        </div>
      </div>

      {/* Right - Image */}
      <div className="hidden lg:block lg:w-[45%] relative">
        <img
          src="https://customer-assets-jai6qajn.emergentagent.net/job_5c6524e8-51a2-4dc4-9807-8677cc8a5c33/artifacts/l8xba8h1_image.png"
          alt="AutEM Fleet"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#002B37]/85 flex flex-col items-center justify-center px-12">
          <div className="text-center">
            <img
              src="/logo_dark.png"
              alt="AutEM"
              className="h-20 object-contain mx-auto mb-6"
            />
            <p className="text-lg text-white/70 leading-relaxed max-w-sm">
              O mercado segue. O AutEM lidera.
              O AutEM que você conhece, como você nunca viu!
            </p>
          </div>
          <div className="mt-12 grid grid-cols-3 gap-6 text-center">
            {[
              { val: "+25 mil", label: "Prestadores conectados" },
              { val: "+R$ 3 bilhões", label: "Gerenciados/ano" },
              { val: "+6 milhões", label: "Serviços/ano" },
            ].map((s) => (
              <div key={s.label}>
                <p className="text-2xl font-bold text-white">{s.val}</p>
                <p className="text-xs text-white/50 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <ForgotPasswordDialog
        open={forgotOpen}
        onOpenChange={setForgotOpen}
        defaultEmail={email}
      />
    </div>
  );
}
